import { cn } from "@au-schein-de/au-ui-library";
import {
	type MouseEvent as ReactMouseEvent,
	type PointerEvent as ReactPointerEvent,
	useRef,
} from "react";
import { useTranslation } from "react-i18next";
import type { FacetCount } from "../filters/types";
import categoryAll from "./assets/category-all.png";
import categoryCapsules from "./assets/category-capsules.png";
import categoryDrinkables from "./assets/category-drinkables.png";
import categoryEdibles from "./assets/category-edibles.png";
import categoryExtracts from "./assets/category-extracts.png";
import categoryFlowers from "./assets/category-flowers.png";
import categoryInhalationsExtract from "./assets/category-inhalations-extract.png";

const CATEGORY_ILLUSTRATIONS: Record<string, string> = {
	flowers: categoryFlowers,
	extracts: categoryExtracts,
	inhalations_extract: categoryInhalationsExtract,
	edibles: categoryEdibles,
	drinkables: categoryDrinkables,
	capsules: categoryCapsules,
};

type CategoryPillsCategory = {
	id: number;
	name: string;
};

type CategoryPillsPlacement = "filters" | "listing";

type CategoryPillsProps = {
	categories: readonly CategoryPillsCategory[];
	value: number[] | undefined;
	onChange: (value: number[] | undefined) => void;
	placement?: CategoryPillsPlacement;
	className?: string;
	categoryFacets?: FacetCount[];
};

type CategoryPillProps = {
	label: string;
	illustration?: string;
	isSelected: boolean;
	isDisabled?: boolean;
	onSelect: () => void;
	testId: string;
	placement: CategoryPillsPlacement;
};

function CategoryPill({
	label,
	illustration,
	isSelected,
	isDisabled = false,
	onSelect,
	testId,
	placement,
}: CategoryPillProps) {
	const isListing = placement === "listing";
	return (
		<button
			type="button"
			aria-pressed={isSelected}
			disabled={isDisabled}
			onClick={onSelect}
			data-testid={testId}
			className={cn(
				"flex shrink-0 snap-start items-center gap-2 overflow-clip rounded-xl text-body-sm font-medium",
				isListing ? "h-10 pl-3 md:h-12 md:pl-4" : "h-12 pl-4",
				isSelected && "bg-ansay-700 text-white",
				!isSelected &&
					!isDisabled &&
					"border-2 border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-200 hover:text-ansay-700",
				isDisabled ? "cursor-default bg-white text-gray-500" : "cursor-pointer",
			)}
		>
			<span className="whitespace-nowrap">{label}</span>
			{illustration ? (
				<img
					src={illustration}
					alt=""
					className={cn(
						"pointer-events-none shrink-0 object-cover",
						isListing ? "h-10 w-10 md:h-12 md:w-12" : "h-12 w-12",
						isDisabled && "grayscale",
					)}
				/>
			) : (
				<span className="w-2" aria-hidden="true" />
			)}
		</button>
	);
}

function toggleCategory(
	value: number[] | undefined,
	categoryId: number,
): number[] | undefined {
	const current = value ?? [];
	if (current.includes(categoryId)) {
		const next = current.filter((id) => id !== categoryId);
		return next.length === 0 ? undefined : next;
	}
	return [...current, categoryId];
}

const DRAG_THRESHOLD_PX = 5;

// The scrollbar is hidden by design, so mouse users have no native way to
// scroll the overflowing pills row — this adds drag-to-scroll for them.
// Touch panning already works natively and is left untouched.
function useMouseDragScroll() {
	const isPointerDownRef = useRef(false);
	const didDragRef = useRef(false);
	const startRef = useRef({ x: 0, scrollLeft: 0 });
	const onPointerDown = (event: ReactPointerEvent<HTMLElement>) => {
		if (event.pointerType !== "mouse") return;
		isPointerDownRef.current = true;
		didDragRef.current = false;
		startRef.current = {
			x: event.clientX,
			scrollLeft: event.currentTarget.scrollLeft,
		};
	};
	const onPointerMove = (event: ReactPointerEvent<HTMLElement>) => {
		if (!isPointerDownRef.current || event.pointerType !== "mouse") return;
		const deltaX = event.clientX - startRef.current.x;
		if (!didDragRef.current && Math.abs(deltaX) < DRAG_THRESHOLD_PX) return;
		didDragRef.current = true;
		event.currentTarget.setPointerCapture(event.pointerId);
		event.currentTarget.scrollLeft = startRef.current.scrollLeft - deltaX;
	};
	const onPointerUp = () => {
		isPointerDownRef.current = false;
	};
	const onClickCapture = (event: ReactMouseEvent<HTMLElement>) => {
		if (!didDragRef.current) return;
		// detail === 0 means a keyboard-generated click (Enter/Space); a stale
		// drag flag from an interrupted drag must not swallow it.
		if (event.detail === 0) {
			didDragRef.current = false;
			return;
		}
		event.preventDefault();
		event.stopPropagation();
		didDragRef.current = false;
	};
	return {
		onPointerDown,
		onPointerMove,
		onPointerUp,
		onPointerCancel: onPointerUp,
		onClickCapture,
	};
}

export function CategoryPills({
	categories,
	value,
	onChange,
	placement = "filters",
	className,
	categoryFacets,
}: CategoryPillsProps) {
	const { t } = useTranslation("product");
	const isAllSelected = !value || value.length === 0;
	const dragScrollHandlers = useMouseDragScroll();
	// Mirrors the Control checkbox filter: a category is disabled only when
	// facet data is present, it has zero matches, and it is not selected.
	const getIsDisabled = (categoryId: number, isSelected: boolean) => {
		if (!categoryFacets?.length || isSelected) return false;
		const count =
			categoryFacets.find((facet) => facet.value === String(categoryId))
				?.count ?? 0;
		return count === 0;
	};
	return (
		<fieldset
			data-testid="category-pills"
			className={cn(
				"flex min-w-0 select-none snap-x gap-2 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
				className,
			)}
			{...dragScrollHandlers}
		>
			<legend className="sr-only">{t("filters.category")}</legend>
			<CategoryPill
				label={t("allProducts")}
				illustration={categoryAll}
				isSelected={isAllSelected}
				onSelect={() => onChange(undefined)}
				testId="category-pill-all"
				placement={placement}
			/>
			{categories.map((category) => {
				const isSelected = value?.includes(category.id) ?? false;
				return (
					<CategoryPill
						key={category.id}
						label={t(`filters.category.${category.name}`, {
							defaultValue: category.name,
						})}
						illustration={CATEGORY_ILLUSTRATIONS[category.name]}
						isSelected={isSelected}
						isDisabled={getIsDisabled(category.id, isSelected)}
						onSelect={() => onChange(toggleCategory(value, category.id))}
						testId={`category-pill-${category.id}`}
						placement={placement}
					/>
				);
			})}
		</fieldset>
	);
}
